<template>
  <div class="purchase-return">
    <ReportChoices :choices="quickRanges" :active="activeRange" @select="applyRange" />

    <div class="rp-toolbar">
      <div class="rp-filters" style="--rp-cols: 5">
        <div class="rp-filter">
          <label>From date</label>
          <input v-model="filters.date_from" type="date" class="form-control" :max="filters.date_to || today" />
        </div>
        <div class="rp-filter">
          <label>To date</label>
          <input v-model="filters.date_to" type="date" class="form-control" :min="filters.date_from" :max="today" />
        </div>
        <div class="rp-filter">
          <label>Supplier</label>
          <FormDropdown v-model="filters.supplier" :options="supplierOptions" placeholder="All suppliers" clearable />
        </div>
        <div class="rp-filter">
          <label>Warehouse</label>
          <FormDropdown v-model="filters.warehouse" :options="warehouseOptions" placeholder="All warehouses" clearable />
        </div>
        <div class="rp-filter">
          <label>Category</label>
          <FormDropdown v-model="filters.category" :options="categoryOptions" placeholder="All categories" clearable />
        </div>
        <div class="rp-filter rp-search">
          <label>Item / Voucher</label>
          <div class="position-relative">
            <i class="ti ti-search rp-search-icon"></i>
            <input v-model="filters.search" type="text" class="form-control"
                   placeholder="Search item, code, return or invoice no." />
          </div>
        </div>
      </div>

      <ReportActions :loading="loading" :exporting="exporting" :can-reset="hasActiveFilters" :can-print="!!rows.length"
                     @reset="resetFilters" @export="exportFile" @print="printReport" />
    </div>

    <div v-if="error" class="alert alert-danger d-flex align-items-center gap-2">
      <i class="ti ti-alert-circle"></i>{{ error }}
    </div>

    <div class="rp-cards" :class="{ 'is-loading': loading }">
      <div class="rp-card tone-red">
        <span class="rp-card-icon"><i class="ti ti-truck-return"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Returned Value</div>
          <div class="rp-card-value">{{ formatMoney(summary.value) }}</div>
          <div class="rp-card-sub">{{ summary.return_rate }}% of {{ formatMoney(summary.purchased) }} purchased</div>
        </div>
      </div>
      <div class="rp-card tone-orange">
        <span class="rp-card-icon"><i class="ti ti-packages"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Quantity</div>
          <div class="rp-card-value">{{ formatQty(summary.qty) }}</div>
          <div class="rp-card-sub">
            {{ plural(summary.return_count, 'return') }} on {{ plural(summary.invoice_count, 'invoice') }}
          </div>
        </div>
      </div>
      <button type="button" class="rp-card rp-card-action tone-teal" :class="{ selected: settle === 'adjusted' }"
              :title="settle === 'adjusted' ? 'Show all returns' : 'Show returns that reduced the due'"
              @click="toggleSettle('adjusted')">
        <span class="rp-card-icon"><i class="ti ti-receipt-off"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Due Adjusted</div>
          <div class="rp-card-value">{{ formatMoney(summary.adjusted_due) }}</div>
          <div class="rp-card-sub">taken off what was owed to suppliers</div>
        </div>
      </button>
      <button type="button" class="rp-card rp-card-action tone-green" :class="{ selected: settle === 'credit' }"
              :title="settle === 'credit' ? 'Show all returns' : 'Show returns that left a supplier credit'"
              @click="toggleSettle('credit')">
        <span class="rp-card-icon"><i class="ti ti-coin"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Supplier Credit</div>
          <div class="rp-card-value">{{ formatMoney(summary.supplier_credit) }}</div>
          <div class="rp-card-sub">beyond the due — suppliers owe this back</div>
        </div>
      </button>
    </div>

    <div class="rp-insights trend-first" :class="{ 'is-loading': loading }">
      <div class="rp-panel">
        <div class="rp-panel-title d-flex justify-content-between align-items-center">
          <span>Returns {{ summary.trend_unit === 'month' ? 'by month' : 'by day' }}</span>
          <span class="rp-legend">
            <span><i class="rp-dot" :style="{ background: RETURN_COLOR }"></i>Value</span>
            <span><i class="rp-dot" :style="{ background: COUNT_COLOR }"></i>Quantity</span>
          </span>
        </div>
        <div v-show="summary.trend.length" class="rp-chart"><canvas ref="trendChart"></canvas></div>
        <p v-if="!summary.trend.length" class="text-muted small mb-0">No returns in this period.</p>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Most returned items</div>
        <ul v-if="summary.top_items.length" class="rp-ranked">
          <li v-for="(item, index) in summary.top_items" :key="item.name">
            <span class="rp-rank">{{ index + 1 }}</span>
            <div class="flex-grow-1 min-w-0">
              <div class="text-truncate" :title="item.name">{{ item.name }}</div>
              <small class="text-muted">{{ formatQty(item.qty) }} returned</small>
            </div>
            <span class="fw-semibold text-nowrap">{{ formatMoney(item.value) }}</span>
          </li>
        </ul>
        <p v-else class="text-muted small mb-0">No returns in this period.</p>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">By supplier</div>
        <div v-if="supplierBars.length" class="rp-bars">
          <div v-for="supplier in supplierBars" :key="supplier.name">
            <div class="d-flex justify-content-between small mb-1">
              <span class="text-truncate me-2">{{ supplier.name }}</span>
              <span class="fw-semibold text-nowrap">
                {{ formatMoney(supplier.value) }}
                <span class="text-muted fw-normal">· {{ plural(supplier.count, 'return') }}</span>
              </span>
            </div>
            <div class="rp-bar-track"><div class="rp-bar-fill danger" :style="{ width: `${supplier.percent}%` }"></div></div>
          </div>
        </div>
        <p v-else class="text-muted small mb-0">No returns in this period.</p>
      </div>
    </div>

    <div class="rp-table-card">
      <div class="rp-table-head">
        <div class="rp-pills">
          <button v-for="pill in settlePills" :key="pill.key" type="button" class="rp-pill"
                  :class="{ active: settle === pill.key }" @click="settle = pill.key">
            {{ pill.label }} <span class="rp-pill-count">{{ formatInt(pill.count) }}</span>
          </button>
        </div>
        <div class="d-flex align-items-center gap-2 small text-muted">
          Show
          <select v-model.number="pageSize" class="form-select form-select-sm rp-page-size">
            <option v-for="size in PAGE_SIZES" :key="size" :value="size">{{ size }}</option>
          </select>
          per page
        </div>
      </div>

      <div class="table-responsive rp-table-wrap">
        <div v-if="loading" class="rp-loading"><span class="spinner-border text-warning"></span></div>
        <table class="table rp-table mb-0">
          <thead>
            <tr>
              <th class="rp-expand-col"></th>
              <th v-for="column in columns" :key="column.key" :class="column.class" @click="sortBy(column.key)">
                <span class="rp-sortable" :class="{ active: sortKey === column.key }">
                  {{ column.label }}<i class="ti" :class="sortIcon(column.key)"></i>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="row in pagedRows" :key="row.key">
              <tr :class="{ expanded: expanded[row.key] }">
                <td class="rp-expand-col">
                  <button type="button" class="rp-expand" :title="expanded[row.key] ? 'Hide items' : 'Show items'"
                          @click="toggleRow(row.key)">
                    <i class="ti" :class="expanded[row.key] ? 'ti-chevron-down' : 'ti-chevron-right'"></i>
                  </button>
                </td>
                <td class="text-nowrap">
                  {{ formatDate(row.date) }}<span v-if="row.time" class="rp-sub">{{ row.time }}</span>
                </td>
                <td class="fw-semibold text-nowrap">{{ row.return_no }}</td>
                <td>
                  <div class="text-nowrap">{{ row.invoice_no }}</div>
                  <span v-if="row.invoice_date" class="rp-sub">bought {{ formatDate(row.invoice_date) }}</span>
                </td>
                <td>
                  <div class="fw-medium">{{ row.supplier }}</div>
                  <span v-if="row.supplier_phone" class="rp-sub">{{ row.supplier_phone }}</span>
                </td>
                <td class="text-center">
                  <span class="rp-count">{{ row.item_count }}</span>
                </td>
                <td class="text-end fw-semibold">{{ formatQty(row.qty) }}</td>
                <td class="text-end fw-semibold">{{ formatMoney(row.value) }}</td>
                <td class="text-end" :class="{ 'text-muted': !Number(row.adjusted_due) }">
                  {{ Number(row.adjusted_due) ? formatMoney(row.adjusted_due) : '—' }}
                </td>
                <td class="text-end" :class="Number(row.supplier_credit) ? 'text-success fw-semibold' : 'text-muted'">
                  {{ Number(row.supplier_credit) ? formatMoney(row.supplier_credit) : '—' }}
                </td>
                <td>
                  <span class="rp-badge" :class="Number(row.supplier_credit) ? 'success' : 'info'">{{ row.settlement }}</span>
                </td>
              </tr>

              <tr v-if="expanded[row.key]" class="rp-detail-row">
                <td></td>
                <td :colspan="columns.length">
                  <div class="rp-detail">
                    <div class="rp-detail-title d-flex flex-wrap justify-content-between gap-2">
                      <span><i class="ti ti-truck-return me-1"></i>Return {{ row.return_no }}</span>
                      <span class="text-muted fw-normal">Recorded by {{ row.created_by || '—' }}</span>
                    </div>
                    <div class="table-responsive">
                      <table class="table table-sm mb-0">
                        <thead>
                          <tr>
                            <th>Item Code</th>
                            <th>Item Name</th>
                            <th>Variant</th>
                            <th>Batch</th>
                            <th>Warehouse</th>
                            <th class="text-end">Qty</th>
                            <th class="text-end">Unit Cost</th>
                            <th class="text-end">Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="line in row.lines" :key="line.key">
                            <td class="fw-semibold">{{ line.item_code }}</td>
                            <td>
                              {{ line.item_name }}
                              <small v-if="line.sku" class="d-block text-muted">SKU: {{ line.sku }}</small>
                            </td>
                            <td>
                              <template v-if="line.variant">
                                <span v-for="part in line.variant.split(' · ')" :key="part" class="rp-variant">{{ part }}</span>
                              </template>
                              <span v-else class="text-muted">—</span>
                            </td>
                            <td>{{ line.batch_no || '—' }}</td>
                            <td>{{ line.warehouse }}</td>
                            <td class="text-end fw-semibold">
                              {{ formatQty(line.qty) }}<small v-if="line.unit" class="text-muted ms-1">{{ line.unit }}</small>
                            </td>
                            <td class="text-end">{{ formatMoney(line.unit_cost) }}</td>
                            <td class="text-end fw-semibold">{{ formatMoney(line.value) }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </td>
              </tr>
            </template>

            <tr v-if="!loading && !pagedRows.length">
              <td :colspan="columns.length + 1">
                <div class="rp-empty">
                  <i class="ti ti-truck-return"></i>
                  <div class="fw-semibold">No purchase returns found</div>
                  <small class="text-muted">Try another period, supplier or search.</small>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="visibleRows.length">
            <tr>
              <th></th>
              <th colspan="5" class="text-end">Total ({{ plural(visibleRows.length, 'return') }})</th>
              <th class="text-end">{{ formatQty(footer.qty) }}</th>
              <th class="text-end">{{ formatMoney(footer.value) }}</th>
              <th class="text-end">{{ formatMoney(footer.adjusted_due) }}</th>
              <th class="text-end text-success">{{ formatMoney(footer.supplier_credit) }}</th>
              <th></th>
            </tr>
          </tfoot>
        </table>
      </div>

      <ReportPagination v-if="visibleRows.length" :page="page" :page-count="pageCount" :pages="pageNumbers"
                        :start="pageStart" :end="pageEnd" :total="visibleRows.length" @go="goToPage" />
    </div>
    <p class="rp-hint mt-2 mb-0">
      Value is the batch cost of the stock sent back. A return first reduces what was owed to the supplier at that
      time; any part beyond it is a supplier credit the supplier owes back.
    </p>
  </div>
</template>

<script>
import apiUrls from '@/urls';
import inventoryReport from '../inventory/inventoryReportMixin';
import periodReport, { defaultPeriod } from '../inventory/periodReportMixin';
import salesReport, { COUNT_COLOR, RETURN_COLOR } from '../sales/salesReportMixin';
import purchaseReport from './purchaseReportMixin';

const EMPTY_SUMMARY = {
  qty: 0, value: 0, adjusted_due: 0, supplier_credit: 0, return_count: 0, supplier_count: 0, invoice_count: 0,
  purchased: 0, return_rate: 0, top_items: [], by_supplier: [], trend_unit: 'day', trend: [],
};
const SUM_FIELDS = ['qty', 'value', 'adjusted_due', 'supplier_credit'];

export default {
  name: 'PurchaseReturnReport',
  mixins: [inventoryReport, periodReport, salesReport, purchaseReport],

  reportUrl: apiUrls.purchase_return_report.get,
  fileName: 'purchase_returns',
  columns: [
    { key: 'date', label: 'Date' },
    { key: 'return_no', label: 'Return No' },
    { key: 'invoice_no', label: 'Purchase Invoice' },
    { key: 'supplier', label: 'Supplier' },
    { key: 'item_count', label: 'Items', class: 'text-center', numeric: true },
    { key: 'qty', label: 'Qty', class: 'text-end', numeric: true },
    { key: 'value', label: 'Value', class: 'text-end', numeric: true },
    { key: 'adjusted_due', label: 'Due Adjusted', class: 'text-end', numeric: true },
    { key: 'supplier_credit', label: 'Supplier Credit', class: 'text-end', numeric: true },
    { key: 'settlement', label: 'Settlement' },
  ],

  data() {
    return {
      filters: {
        ...defaultPeriod(), supplier: null, warehouse: null, category: null, sub_category: null, search: '',
      },
      summary: { ...EMPTY_SUMMARY },
      settle: 'all',
      sortKey: null,
      sortDir: 'desc',
      RETURN_COLOR,
      COUNT_COLOR,
    };
  },

  computed: {
    hasActiveFilters() {
      return this.periodChanged || this.itemFiltersActive || !!this.filters.supplier || this.settle !== 'all';
    },

    settlePills() {
      return [
        { key: 'all', label: 'All returns', count: this.rows.length },
        { key: 'adjusted', label: 'Due adjusted', count: this.rows.filter((r) => Number(r.adjusted_due) > 0).length },
        { key: 'credit', label: 'Supplier credit', count: this.rows.filter((r) => Number(r.supplier_credit) > 0).length },
      ];
    },

    supplierBars() {
      return this.withPercent(this.summary.by_supplier, 'value');
    },

    visibleRows() {
      let rows = this.rows;
      if (this.settle === 'adjusted') rows = rows.filter((r) => Number(r.adjusted_due) > 0);
      if (this.settle === 'credit') rows = rows.filter((r) => Number(r.supplier_credit) > 0);
      return this.sortKey ? this.sortRows(rows) : rows;
    },

    footer() {
      return Object.fromEntries(SUM_FIELDS.map((f) => [f, this.visibleRows.reduce((s, r) => s + (Number(r[f]) || 0), 0)]));
    },
  },

  watch: {
    settle() { this.page = 1; },
  },

  methods: {
    extraParams() {
      return { ...this.periodParams(), ...this.supplierParams() };
    },

    resetExtraFilters() {
      this.sortKey = null;
      this.settle = 'all';
      return { ...defaultPeriod(this.today), supplier: null };
    },

    applyResult(data) {
      this.summary = { ...EMPTY_SUMMARY, ...(data?.summary || {}) };
      this.$nextTick(() => this.drawMoneyTrend(this.$refs.trendChart, [
        { label: 'Value', field: 'value', color: RETURN_COLOR },
        { label: 'Quantity', field: 'qty', color: COUNT_COLOR, count: true },
      ]));
    },

    toggleSettle(key) {
      this.settle = this.settle === key ? 'all' : key;
    },

    printReport() {
      const s = this.summary;
      const f = this.footer;
      const settle = this.settlePills.find((p) => p.key === this.settle)?.label;
      this.printColumns(
        'Purchase Return Register',
        [this.periodText(), `Showing: ${settle}`, this.supplierText(), ...this.itemFiltersText()],
        [
          ['Returned', `${this.formatMoney(s.value)} (${s.return_rate}% of purchases)`],
          ['Quantity', `${this.formatQty(s.qty)} · ${this.plural(s.return_count, 'return')}`],
          ['Due Adjusted', this.formatMoney(s.adjusted_due)],
          ['Supplier Credit', this.formatMoney(s.supplier_credit)],
        ],
        [
          { label: 'Date', value: (r) => `${this.formatDate(r.date)} ${r.time}` },
          { label: 'Return No', value: (r) => r.return_no },
          { label: 'Invoice', value: (r) => r.invoice_no },
          { label: 'Supplier', value: (r) => r.supplier },
          { label: 'Items', value: (r) => r.lines.map((l) => `${l.item_name}${l.variant ? ` (${l.variant})` : ''} × ${this.formatQty(l.qty)}`).join(', ') },
          { label: 'Qty', value: (r) => this.formatQty(r.qty), num: true },
          { label: 'Value', value: (r) => this.formatMoney(r.value), num: true },
          { label: 'Due Adjusted', value: (r) => this.formatMoney(r.adjusted_due), num: true },
          { label: 'Supplier Credit', value: (r) => this.formatMoney(r.supplier_credit), num: true },
        ],
        this.visibleRows,
        {
          Items: 'Total', Qty: this.formatQty(f.qty), Value: this.formatMoney(f.value),
          'Due Adjusted': this.formatMoney(f.adjusted_due), 'Supplier Credit': this.formatMoney(f.supplier_credit),
        },
      );
    },
  },
};
</script>

<style scoped src="../inventory/inventoryReport.css"></style>
